import { useSyncExternalStore } from 'react';
import type { ImportCompletedPayload } from '@klareospace/pharos-lib-types';

// ─── Constants ──────────────────────────────────────────────────────────────

/** How long the pending notification card stays visible before auto-dismissing. */
export const IMPORT_PENDING_DURATION_MS = 5000;

// ─── Types ────────────────────────────────────────────────────────────────────

export type ImportNotification =
  { importId: string; kind: 'pending' } | { kind: 'completed'; payload: ImportCompletedPayload };

// ─── Module-level single-slot store ────────────────────────────────────────────
//
// Exactly one notification is visible at a time — a newer one replaces it, so a completion
// naturally supersedes a pending card for the same import. Module-level (not React context) so
// `useImportFile` and `ImportNotificationListener` can share it without a common ancestor.
//
// Monotonicity: a completion can arrive (realtime) before the upload response resolves, so a late
// `showImportPending()` for the same import must not clobber it. `terminalImportIds` tracks that
// independently of the visible slot, so the guarantee survives the card being dismissed.

let visibleNotification: ImportNotification | null = null;
const terminalImportIds = new Set<string>();
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) {
    listener();
  }
}

/**
 * Shows the "import in progress" pending card, replacing whichever notification (if any) is
 * currently visible. No-op when `importId` has already reached a terminal state — see the
 * monotonicity guarantee above.
 */
export function showImportPending(importId: string): void {
  if (terminalImportIds.has(importId)) return;
  visibleNotification = { importId, kind: 'pending' };
  emit();
}

/**
 * Shows a completion payload, replacing whichever notification (if any) is currently visible, and
 * records the import as terminal so a late `showImportPending()` for the same id is ignored.
 */
export function showImportCompletion(payload: ImportCompletedPayload): void {
  terminalImportIds.add(payload.importId);
  visibleNotification = { kind: 'completed', payload };
  emit();
}

/** Dismisses the currently visible notification, if any. Does not clear `terminalImportIds`. */
export function dismissImportNotification(): void {
  visibleNotification = null;
  emit();
}

/** Clears the visible slot and the terminal-id set — full reset for a new session (e.g. sign-out). */
export function resetImportNotifications(): void {
  visibleNotification = null;
  terminalImportIds.clear();
  emit();
}

/** Synchronous read of the currently visible notification. */
function getVisibleImportNotification(): ImportNotification | null {
  return visibleNotification;
}

/** Registers a listener notified whenever the visible notification changes. Returns an unsubscribe function. */
function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return (): void => {
    listeners.delete(listener);
  };
}

/** React binding — re-renders the consuming component when the visible notification changes. */
export function useVisibleImportNotification(): ImportNotification | null {
  return useSyncExternalStore(subscribe, getVisibleImportNotification, getVisibleImportNotification);
}
